import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { fadeIn, slideUp } from '@/utils/animations';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, FileText, Scale, Shield } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-green-50">
      <Header />
      <motion.main
        className="flex-1"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="container mx-auto px-4 py-12">
          <motion.h1
            className="text-4xl font-bold text-green-800 mb-8 text-center"
            variants={slideUp}
          >
            Terms of Service
          </motion.h1>

          {/* Introduction */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <FileText className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Introduction</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Welcome to PermitTree. By accessing or using our permit management system, you agree to be bound by these Terms of Service. Please read these terms carefully before using our services.
            </p>
            <p className="text-gray-600">
              Last updated: February 15, 2024
            </p>
          </motion.section>

          {/* Acceptance of Terms */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <CheckCircle className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Acceptance of Terms</h2>
            </div>
            <p className="text-gray-600 mb-4">
              By accessing and using PermitTree, you acknowledge that you have read, understood, and agree to be bound by these terms. If you do not agree with any part of these terms, you must not use our services.
            </p>
            <div className="space-y-4 mt-4">
              <h3 className="text-xl font-semibold text-green-700">Service Eligibility</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>You must be at least 18 years old</li>
                <li>You must provide accurate and complete registration information</li>
                <li>You must have the legal capacity to enter into agreements</li>
                <li>You must comply with all applicable laws and regulations</li>
              </ul>
            </div>
          </motion.section>

          {/* User Responsibilities */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <Scale className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">User Responsibilities</h2>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-green-700">Account Security</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Take responsibility for all activities under your account</li>
                <li>Keep your contact information updated</li>
              </ul>

              <h3 className="text-xl font-semibold text-green-700 mt-6">Prohibited Activities</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>Submitting false or misleading information</li>
                <li>Attempting to circumvent security measures</li>
                <li>Using the service for illegal activities</li>
                <li>Interfering with service operations</li>
                <li>Sharing account access with unauthorized users</li>
              </ul>
            </div>
          </motion.section>

          {/* Service Terms */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <Shield className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Service Terms</h2>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-green-700">Permit Applications</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>All applications must be complete and accurate</li>
                <li>Processing times are estimates and not guaranteed</li>
                <li>Fees are non-refundable once processing begins</li>
                <li>Additional documentation may be required</li>
              </ul>

              <h3 className="text-xl font-semibold text-green-700 mt-6">Service Availability</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>We strive for 24/7 availability but cannot guarantee uninterrupted service</li>
                <li>Maintenance windows may affect availability</li>
                <li>Force majeure events may impact service delivery</li>
              </ul>
            </div>
          </motion.section>

          {/* Disclaimers and Limitations */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <AlertTriangle className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Disclaimers and Limitations</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600">
                Our services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that:
              </p>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>The service will be error-free or uninterrupted</li>
                <li>Any defects will be corrected</li>
                <li>The service is free of viruses or harmful components</li>
                <li>Results obtained through the service will be accurate or reliable</li>
              </ul>
            </div>
          </motion.section>

          {/* Changes to Terms */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <h2 className="text-2xl font-semibold text-green-800 mb-6">Changes to Terms</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify these terms at any time. We will notify users of any material changes through:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
              <li>Email notifications</li>
              <li>Service announcements</li>
              <li>Website updates</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Continued use of the service after changes constitutes acceptance of the modified terms.
            </p>
          </motion.section>

          {/* Contact Information */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <h2 className="text-2xl font-semibold text-green-800 mb-6">Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="text-gray-600">
              <p>Email: legal@permittree.com</p>
              <p>Phone: (123) 456-7890</p>
              <p>Address: DENR-PENRO Office, Boac, Marinduque</p>
            </div>
          </motion.section>
        </div>
      </motion.main>
      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
